import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { catchError, first, switchMap, tap } from 'rxjs/operators';

import { throwError, Observable } from 'rxjs';
import { AppNotification } from '@deltager/organizers/admin/shared/models/AppNotification';
import { AppNotificationType } from '@deltager/organizers/admin/shared/models/AppNotificationType';
import { ApiError } from '@deltager/organizers/admin/core/models/apiError.model';

import { AppNotificationsService } from '@deltager/organizers/admin//core/services/app-notifications.service';
import { AuthService } from '../auth/auth.service';
import { Store } from '@ngrx/store';
import { RootState, selectOrganizer } from '../../redux/root.state';
import { OrganizerSettings } from '../../shared/models/OrganizerSettings';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { GTMService } from '@deltager/organizers/admin/core/services/gtm.service';

export const ApiInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const excludedUrls: Array<string> = [
    'https://staging.letsreg.com',
    'https://www.letsreg.com',
    'https://www.letsreg.co',
  ];

  // Ignore some requests
  if (excludedUrls.some(url => request.url.startsWith(url))) {
    return next(request);
  }

  const authService = inject(AuthService);
  const notificationService = inject(AppNotificationsService);
  const store = inject(Store<RootState>);
  const appSettingsService = inject(AppSettingsService);
  const gtmService = inject(GTMService);

  const authToken = authService.getAuthToken();
  if (authToken && authToken !== '') {
    request = addHeader(request, 'Authorization', authService.getAuthToken());
  }

  if (appSettingsService.affiliateId) {
    request = addHeader(request, 'AffiliateId', appSettingsService.affiliateId.toString());
  }

  // Add GA4 Session ID
  const ga4SessionId = gtmService.getGA4SessionId();
  if (ga4SessionId) {
    request = addHeader(request, 'X-GA4-Session-ID', ga4SessionId);
  }

  // Add GA4 Client ID
  const ga4ClientId = gtmService.getGA4ClientId();
  if (ga4ClientId) {
    request = addHeader(request, 'X-GA4-Client-ID', ga4ClientId);
  }

  return store.select(selectOrganizer).pipe(
    first(),
    tap((organizer: OrganizerSettings) => {
      if (organizer?.id) {
        request = addHeader(request, 'OrganizerId', organizer.id.toString());
      }

      const parsedUrl = parseUrl();

      if (parsedUrl.eventId) {
        request = addHeader(request, 'EventId', parsedUrl.eventId);
      }

      if (parsedUrl.bookingEventId) {
        request = addHeader(request, 'BookingEventId', parsedUrl.bookingEventId.toString());
      }
    }),
    switchMap(() =>
      next(request).pipe(
        catchError((response: HttpErrorResponse) => {
          const is500Error = response.status === 500;

          if (is500Error || response.status === 400 || response.status === 403) {
            let notificationModel: AppNotification = {
              type: AppNotificationType.ERROR,
            };

            if (response.error?.errors) {
              const errorMessages: string[] = [];

              if (Array.isArray(response.error.errors)) {
                response.error.errors.forEach((error: any) => {
                  if (error.errorMessage) {
                    errorMessages.push(error.errorMessage);
                  }
                });
              }
              notificationModel.message = errorMessages.join(' ');
            } else if (response.error) {
              const error: ApiError = response.error;

              if (response.status === 400 || response.status === 403) {
                notificationModel.message = error.detail;
              } else {
                notificationModel.title = error.error_description ?? error.message;
              }
            } else if (response.message) {
              notificationModel.message = response.message;
            }

            notificationService.sendError(notificationModel);
          }

          return throwError(() => response);
        }),
      ),
    ),
  );
};

function addHeader(request: HttpRequest<any>, key: string, value: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      [key]: value,
    },
  });
}

function parseUrl(): { eventId: string; bookingEventId: string } {
  const url = window.location.href;

  const extractEventId = (url: string): string | null => {
    const match = url.match(/#\/events(?:\/\w+)*\/(\d+)/);
    return match ? match[1] : null;
  };

  const extractBookingEventId = (url: string): string | null => {
    const match = url.match(/#\/booking-events\/(\d+)/);
    return match ? match[1] : null;
  };

  return {
    eventId: extractEventId(url),
    bookingEventId: extractBookingEventId(url),
  };
}
